import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtNota, formativaBadge } from "@/lib/constants";
import {
  FolderKanban, GraduationCap, Users, ArrowRight, Check, Loader2,
  BarChart3, Settings2, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

// Wizard: Curso (com modelo formativo) -> Turma -> Aluno -> cria/retoma avaliação do instrutor
export default function Formativa() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [step, setStep] = useState(0);
  const [cursos, setCursos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [sel, setSel] = useState({ curso: null, turma: null });
  const [creating, setCreating] = useState(false);
  const [loadingCursos, setLoadingCursos] = useState(true);

  useEffect(() => {
    api.get("/formativa/cursos")
      .then((r) => setCursos(r.data))
      .finally(() => setLoadingCursos(false));
  }, []);

  // Retomar a lista de alunos da mesma turma ao voltar da ficha do aluno
  useEffect(() => {
    const st = location.state;
    if (st?.turmaId && st?.cursoId) {
      setSel({ curso: { id: st.cursoId, nome: st.cursoNome || "" }, turma: { id: st.turmaId, nome: st.turmaNome || "" } });
      api.get(`/turmas?curso_id=${st.cursoId}`).then((r) => setTurmas(r.data));
      api.get(`/alunos?turma_id=${st.turmaId}`).then((r) => { setAlunos(r.data); setStep(2); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickCurso = (c) => {
    setSel({ curso: c, turma: null });
    api.get(`/turmas?curso_id=${c.id}`).then((r) => { setTurmas(r.data); setStep(1); });
  };
  const pickTurma = (t) => {
    setSel((s) => ({ ...s, turma: t }));
    api.get(`/alunos?turma_id=${t.id}`).then((r) => { setAlunos(r.data); setStep(2); });
  };

  const start = async (aluno) => {
    setCreating(true);
    try {
      const { data } = await api.post("/formativa/avaliacoes", {
        turma_id: sel.turma.id, aluno_id: aluno.id,
      });
      navigate(`/formativa/avaliar/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setCreating(false); }
  };

  const steps = ["Curso", "Turma", "Aluno"];

  return (
    <div>
      <PageHeader title="Avaliação Formativa" subtitle="Escala 0 a 10 · avaliação independente por instrutor · Curso de Negociação">
        {isAdmin && (
          <Button variant="secondary" data-testid="formativa-modelo-btn" onClick={() => navigate("/formativa/modelo")}>
            <Settings2 className="h-4 w-4 mr-2" /> Modelo base
          </Button>
        )}
      </PageHeader>

      <div className="flex items-center flex-wrap gap-2 mb-6 text-sm font-mono">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <button
              data-testid={`fstep-${s.toLowerCase()}`}
              disabled={i > step}
              onClick={() => i < step && setStep(i)}
              className={`px-3 py-1.5 rounded-md border transition-colors max-w-full whitespace-normal break-words text-left ${
                i === step ? "bg-primary text-primary-foreground border-primary"
                : i < step ? "bg-secondary text-foreground border-border hover:bg-accent"
                : "text-muted-foreground border-border opacity-50"
              }`}
            >
              {i + 1}. {s}
              {i === 0 && sel.curso ? `: ${sel.curso.nome}` : ""}
              {i === 1 && sel.turma ? `: ${sel.turma.nome}` : ""}
            </button>
            {i < steps.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        loadingCursos ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : !cursos.length ? (
          <Card className="p-8 text-center bg-card border-border">
            <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">
              Nenhum curso com modelo de Avaliação Formativa disponível para você.
              {isAdmin && " Configure o modelo base para habilitar."}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cursos.map((c) => (
              <button key={c.id} data-testid={`fpick-curso-${c.id}`} onClick={() => pickCurso(c)}
                className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary hover:bg-card/70 transition-all text-left active:scale-[0.98]">
                <div className="p-2 rounded-lg bg-secondary"><FolderKanban className="h-5 w-5 text-sky-400" /></div>
                <div className="min-w-0"><div className="font-semibold truncate">{c.nome}</div></div>
                <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
              </button>
            ))}
          </div>
        )
      )}

      {step === 1 && (
        <>
          {!turmas.length && <p className="text-muted-foreground text-sm">Nenhuma turma neste curso.</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {turmas.map((t) => (
              <div key={t.id} className="rounded-lg border border-border bg-card overflow-hidden">
                <button data-testid={`fpick-turma-${t.id}`} onClick={() => pickTurma(t)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-card/70 transition-all text-left active:scale-[0.98]">
                  <div className="p-2 rounded-lg bg-secondary"><GraduationCap className="h-5 w-5 text-purple-400" /></div>
                  <div className="min-w-0"><div className="font-semibold truncate">{t.nome}</div></div>
                  <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
                </button>
                <button data-testid={`fconsolidado-${t.id}`} onClick={() => navigate(`/formativa/consolidado/${t.id}`)}
                  className="w-full flex items-center gap-2 px-4 py-2.5 border-t border-border text-xs text-primary hover:bg-secondary/60 transition-colors">
                  <BarChart3 className="h-4 w-4" /> Ver consolidado da turma
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {alunos.map((a) => (
            <button key={a.id} data-testid={`fpick-aluno-${a.id}`} disabled={creating} onClick={() => start(a)}
              className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary hover:bg-card/70 transition-all text-left active:scale-[0.98]">
              <div className="p-2 rounded-full bg-amber-500/15"><Users className="h-5 w-5 text-amber-400" /></div>
              <div className="min-w-0">
                <div className="font-semibold truncate">{a.nome}</div>
                {a.nome_guerra && <div className="text-xs text-primary font-mono truncate">"{a.nome_guerra}"</div>}
              </div>
              {creating ? <Loader2 className="h-4 w-4 ml-auto animate-spin" /> : <Check className="h-4 w-4 ml-auto text-muted-foreground" />}
            </button>
          ))}
          {!alunos.length && <p className="text-muted-foreground text-sm">Nenhum aluno nesta turma.</p>}
        </div>
      )}
    </div>
  );
}
